import { axiosInstance } from '../../api/api';
import {
	MAJOR_REPAIR_API_PATHS,
	MAJOR_REPAIR_MESSAGES,
	OPERATIONS,
} from '../../constants';
import { updateReceivePart } from './receivePart';
import { updateCurrentLineItem, updateLastLineItem } from './station';

/**
 * Action creator to get all vehicles currently in MR
 * GET /mr/station/all-vehicles
 */
export const fetchAllMrVehicles =
	(successCallback, errorCallback) => () => {
		axiosInstance
			.get(MAJOR_REPAIR_API_PATHS.ALL_VEHICLES)
			.then(({ data }) => {
				const vehicles = Array.isArray(data) ? data : [];
				if (typeof successCallback === 'function') {
					successCallback(vehicles);
				}
			})
			.catch((err) => {
				if (typeof errorCallback === 'function') {
					errorCallback(err);
				}
			});
	};

/**
 * Fetch MR vehicle history. When no range is supplied, the request body is
 * deliberately omitted so the backend applies its default last-24-hours range.
 * POST /mr/station/vehicles-by-time-range
 */
export const fetchMrVehicleHistory =
	(startTs, endTs, successCallback, errorCallback) => () => {
		const request = startTs && endTs
			? axiosInstance.post(MAJOR_REPAIR_API_PATHS.VEHICLES_BY_TIME_RANGE, { startTs, endTs })
			: axiosInstance.post(MAJOR_REPAIR_API_PATHS.VEHICLES_BY_TIME_RANGE);

		request
			.then(({ data }) => {
				if (typeof successCallback === 'function') {
					successCallback(Array.isArray(data) ? data : []);
				}
			})
			.catch((err) => {
				if (typeof errorCallback === 'function') {
					errorCallback(err);
				}
			});
	};

/**
 * Clear the current vehicle selection at the MR station.
 * POST /mr/station/clear-vin
 */
export const clearMrVin =
	(successCallback, errorCallback) => (dispatch) => {
		axiosInstance
			.post(MAJOR_REPAIR_API_PATHS.CLEAR_VIN)
			.then(() => {
				dispatch(updateCurrentLineItem(null));
				dispatch(updateReceivePart({}));
				if (typeof successCallback === 'function') {
					successCallback();
				}
			})
			.catch((err) => {
				if (typeof errorCallback === 'function') {
					errorCallback(err);
				}
			});
	};

/**
 * Action creator to send a vehicle to MR directly from line station (station-operator action)
 * POST /station/add-to-mr
 */
export const addToMr =
	(asn, reason, successCallback, errorCallback) => (dispatch, getState) => {
		const { user } = getState();
		const dtStationId = user?.stationId || user?.station || null;
		const dtLineId = user?.lineId || user?.line || null;

		const payload = {
			asn,
			dtStationId,
			dtLineId,
			reason: reason || MAJOR_REPAIR_MESSAGES.ADD_TO_MR_REASON,
		};

		axiosInstance
			.post(MAJOR_REPAIR_API_PATHS.ADD_TO_MR, payload)
			.then(({ data }) => {
				dispatch(updateCurrentLineItem(null));
				dispatch(updateReceivePart({}));
				if (typeof successCallback === 'function') {
					successCallback(data);
				}
			})
			.catch((err) => {
				const status = err?.response?.status;
				let customErr = err;
				if (status === 500) {
					const msg =
						err?.response?.data?.message ||
						MAJOR_REPAIR_MESSAGES.NOT_IN_FLIGHT;
					customErr = {
						...err,
						response: {
							...err?.response,
							data: { message: msg },
						},
					};
				}
				if (typeof errorCallback === 'function') {
					errorCallback(customErr);
				}
			});
	};

/**
 * Action creator to notify MR that a vehicle has physically arrived / been scanned
 * POST /mr/station/received-asn/{sequenceNumber}
 */
export const receiveMrPart =
	(sequenceNumber, successCallback, errorCallback) => (dispatch, getState) => {
		const {
			station: { currentLineItem },
		} = getState();

		axiosInstance
			.post(`${MAJOR_REPAIR_API_PATHS.RECEIVED_ASN}/${sequenceNumber}`)
			.then(async () => {
				dispatch(updateReceivePart({ serialNumber: sequenceNumber }));
				// MR station does not use /station/receive-details

				try {
					const { data } = await axiosInstance.get(MAJOR_REPAIR_API_PATHS.CURRENT_PART);
					if (data) {
						dispatch(
							updateCurrentLineItem({
								...currentLineItem,
								...data,
								serialNumber: data.serialNo || data.serialNumber,
								sequenceNumber: data.asn || data.sequenceNumber,
								completedOperations: [
									...new Set([
										...(currentLineItem.completedOperations || []),
										...(data.completedOperations || []),
										OPERATIONS.RECEIVE_PART,
									]),
								],
							}),
						);
					} else {
						dispatch(
							updateCurrentLineItem({
								...currentLineItem,
								sequenceNumber,
								serialNumber: sequenceNumber,
								completedOperations: [
									...new Set([
										...(currentLineItem.completedOperations || []),
										OPERATIONS.RECEIVE_PART,
									]),
								],
							}),
						);
					}
					successCallback();
				} catch (err) {
					dispatch(
						updateCurrentLineItem({
							...currentLineItem,
							sequenceNumber,
							serialNumber: sequenceNumber,
							completedOperations: [
								...new Set([
									...(currentLineItem.completedOperations || []),
									OPERATIONS.RECEIVE_PART,
								]),
							],
						}),
					);
					errorCallback(err);
				}
			})
			.catch((err) => {
				const status = err?.response?.status;
				let customErr = err;
				if (status === 404) {
					const msg =
						err?.response?.data?.message ||
						MAJOR_REPAIR_MESSAGES.NO_ACTIVE_RECORD;
					customErr = {
						...err,
						response: {
							...err?.response,
							data: { message: msg },
						},
					};
				}
				errorCallback(customErr);
			});
	};

/**
 * Action creator to fetch available destination stations for MR Move Out
 * GET /mr/station/destinations
 */
export const fetchMrDestinationStations =
	(sequenceNumber, successCallback, errorCallback) => () => {
		axiosInstance
			.get(`${MAJOR_REPAIR_API_PATHS.DESTINATIONS}/${sequenceNumber}`)
			.then(({ data }) => {
				const stations = data?.validStations || [];
				successCallback(stations);
			})
			.catch((err) => {
				let customErr = err;
				if (err?.response?.status === 500) {
					customErr = {
						...err,
						response: {
							...err?.response,
							data: { message: MAJOR_REPAIR_MESSAGES.DESTINATION_NOT_FOUND },
						},
					};
				}
				if (typeof errorCallback === 'function') {
					errorCallback(customErr);
				}
			});
	};

/**
 * Action creator to perform MOVE OUT for Major Repair station
 * POST /mr/station/move-to-line
 */
export const moveOutMrStation =
	(sequenceNumber, destinationStationId, destinationLineId, successCallback, errorCallback) => (dispatch, getState) => {
		const {
			station: { currentLineItem },
		} = getState();

		const payload = {
			asn: sequenceNumber,
			mrToLineDtStationId: destinationStationId,
			mrToLineDtLineId: destinationLineId || null,
			mrToLineReason: MAJOR_REPAIR_MESSAGES.MOVE_OUT_REASON,
		};

		axiosInstance
			.post(MAJOR_REPAIR_API_PATHS.MOVE_TO_LINE, payload)
			.then(() => {
				if (currentLineItem && Object.keys(currentLineItem).length > 0) {
					dispatch(updateLastLineItem(currentLineItem));
				}
				dispatch(updateCurrentLineItem(null));
				dispatch(updateReceivePart({}));
				if (typeof successCallback === 'function') {
					successCallback();
				}
			})
			.catch((err) => {
				let customErr = err;
				if (err?.response?.status === 500) {
					customErr = {
						...err,
						response: {
							...err?.response,
							data: {
								message:
									err?.response?.data?.message ||
									MAJOR_REPAIR_MESSAGES.MOVE_OUT_FAILED,
							},
						},
					};
				}
				if (typeof errorCallback === 'function') {
					errorCallback(customErr);
				}
			});
	};
